import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Paperclip, Send, X, Trash2, Copy, CheckSquare, FileText, Loader2, Users, Ban } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { EkycMembersDialog } from "@/components/EkycMembers";

const DAY_MS = 24 * 3600 * 1000;
const istDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const timeOf = (iso) => new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true });
function dayLabel(iso) {
  const d = istDay(iso);
  const today = istDay(new Date().toISOString());
  const yest = istDay(new Date(Date.now() - DAY_MS).toISOString());
  if (d === today) return "Aaj";
  if (d === yest) return "Kal";
  return new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric" });
}

export function EkycChat({ initialGroupId }) {
  const [groups, setGroups] = useState(null);
  const [activeId, setActiveId] = useState(initialGroupId || "");
  const load = useCallback(() => api.get("/ekyc/chat/groups").then(({ data }) => setGroups(data.groups)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  const active = groups?.find((g) => g.id === activeId);

  if (groups === null) return <div className="py-10 text-center text-sm text-[#6B6B61]"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  if (groups.length === 0) return <div className="py-10 text-center text-sm text-[#6B6B61]" data-testid="ekyc-chat-no-groups">Koi EKYC group assign nahi hai.</div>;
  return (
    <div className="bg-white border border-[#EDEDE5] rounded-lg overflow-hidden flex h-[calc(100vh-130px)] min-h-[420px]" data-testid="ekyc-chat">
      <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-72 flex-col border-r border-[#EDEDE5]`}>
        <div className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B] border-b border-[#EDEDE5]">EKYC Groups</div>
        <div className="flex-1 overflow-y-auto divide-y divide-[#F0F0EA]">
          {groups.map((g) => (
            <button key={g.id} onClick={() => setActiveId(g.id)} data-testid={`ekyc-chat-group-${g.id}`}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-[#F5F5F0] ${g.id === activeId ? "bg-[#EEF3EF]" : ""}`}>
              <div className="w-9 h-9 rounded-full bg-[#2F6F8F] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{g.company[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{g.name}</div>
                <div className="text-xs text-[#6B6B61] truncate">{previewOf(g.last_message)}</div>
              </div>
              {g.unread > 0 && <span className="text-[10px] bg-[#25A244] text-white rounded-full px-1.5 py-0.5 font-bold" data-testid={`ekyc-chat-unread-${g.id}`}>{g.unread}</span>}
            </button>
          ))}
        </div>
      </div>
      {active ? (
        <ChatPane key={active.id} group={active} onBack={() => setActiveId("")} onActivity={load} />
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-sm text-[#6B6B61]">Group select karo</div>
      )}
    </div>
  );
}

function previewOf(m) {
  if (!m) return "No messages yet";
  if (m.deleted) return "🚫 Message deleted";
  const who = m.sender_name ? `${m.sender_name}: ` : "";
  if (m.record_card) return `${who}📋 ${m.record_card.name || m.record_card.consumer_id}`;
  if (m.attachments?.length && !m.text) return `${who}📎 ${m.attachments[0].name}`;
  return who + m.text;
}

function ChatPane({ group, onBack, onActivity }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [menu, setMenu] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const bottomRef = useRef(null);
  const lastIdRef = useRef("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ekyc/chat/${group.id}/messages`);
      setMsgs(data.messages);
      const last = data.messages[data.messages.length - 1]?.id || "";
      if (last !== lastIdRef.current) {
        lastIdRef.current = last;
        api.post(`/ekyc/chat/${group.id}/read`).then(onActivity).catch(() => {});
        setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 50);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [group.id, onActivity]);
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const canDelete = (m) => !m.deleted && (user.role === "admin" || (m.sender_id === user.id && Date.now() - new Date(m.created_at).getTime() < DAY_MS));
  const selMsgs = (msgs || []).filter((m) => selected.has(m.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const del = async (ids) => {
    if (!window.confirm(`${ids.length} message delete karein? (sabke liye)`)) return;
    try {
      await api.post(`/ekyc/chat/${group.id}/delete`, { ids });
      setSelected(new Set());
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const copy = (list) => {
    navigator.clipboard.writeText(list.map((m) => m.text).filter(Boolean).join("\n")).then(() => toast.success("Copied")).catch(() => {});
  };

  return (
    <div className="flex-1 flex flex-col min-w-0" data-testid="ekyc-chat-pane">
      {selected.size > 0 ? (
        <div className="px-3 py-2.5 bg-[#385C4B] text-white flex items-center gap-3" data-testid="ekyc-chat-select-bar">
          <button onClick={() => setSelected(new Set())} data-testid="ekyc-chat-select-cancel"><X className="w-5 h-5" /></button>
          <span className="font-bold" data-testid="ekyc-chat-select-count">{selected.size} selected</span>
          <button onClick={() => copy(selMsgs)} className="ml-auto p-1.5 hover:bg-white/10 rounded" title="Copy" data-testid="ekyc-chat-select-copy"><Copy className="w-4 h-4" /></button>
          <button onClick={() => del([...selected])} disabled={!selMsgs.every(canDelete)} title={selMsgs.every(canDelete) ? "Delete" : "Sirf apne 24 ghante ke andar wale message"}
            className="p-1.5 hover:bg-white/10 rounded disabled:opacity-40" data-testid="ekyc-chat-select-delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="px-3 py-2.5 border-b border-[#EDEDE5] flex items-center gap-2">
          <button onClick={onBack} className="md:hidden p-1" data-testid="ekyc-chat-back"><ArrowLeft className="w-5 h-5 text-[#385C4B]" /></button>
          <div className="w-8 h-8 rounded-full bg-[#2F6F8F] text-white flex items-center justify-center text-xs font-bold">{group.company[0]}</div>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" data-testid="ekyc-chat-title">{group.name}</div>
            <div className="text-[11px] text-[#6B6B61] flex items-center gap-1" data-testid="ekyc-chat-member-count"><Users className="w-3 h-3" />{group.member_count} members · {group.company}</div>
          </div>
          {user.role === "admin" && (
            <button onClick={() => setShowMembers(true)} data-testid="ekyc-chat-members-btn"
              className="ml-auto text-xs font-mono uppercase px-3 py-1.5 rounded border border-[#385C4B] text-[#385C4B] flex items-center gap-1 hover:bg-[#385C4B]/5">
              <Users className="w-3.5 h-3.5" />Members
            </button>
          )}
        </div>
      )}
      {showMembers && <EkycMembersDialog group={group} onClose={() => setShowMembers(false)} onChanged={onActivity} />}
      <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-3 bg-[#EFEAE2]" data-testid="ekyc-chat-messages">
        {msgs === null ? <div className="text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          : msgs.length === 0 ? <div className="text-center text-xs text-[#6B6B61] mt-10">Abhi koi message nahi. Pehla message bhejo 👋</div>
          : msgs.map((m, i) => (
            <React.Fragment key={m.id}>
              {(i === 0 || istDay(msgs[i - 1].created_at) !== istDay(m.created_at)) && (
                <div className="text-center my-2"><span className="text-[11px] bg-white/90 text-[#6B6B61] px-2.5 py-1 rounded-md shadow-sm">{dayLabel(m.created_at)}</span></div>
              )}
              <Bubble m={m} mine={m.sender_id === user.id} selected={selected.has(m.id)} selecting={selected.size > 0}
                onToggle={() => toggle(m.id)}
                onMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, m }); }} />
            </React.Fragment>
          ))}
        <div ref={bottomRef} />
      </div>
      <Composer group={group} onSent={load} />
      {menu && (
        <BubbleMenu anchor={menu} onClose={() => setMenu(null)}
          onSelect={() => toggle(menu.m.id)}
          onCopy={menu.m.text ? () => copy([menu.m]) : null}
          onDelete={canDelete(menu.m) ? () => del([menu.m.id]) : null} />
      )}
    </div>
  );
}

function Bubble({ m, mine, selected, selecting, onToggle, onMenu }) {
  const longPress = useRef(null);
  const touchStart = (e) => {
    const t = e.touches[0];
    longPress.current = setTimeout(() => onMenu({ preventDefault() {}, clientX: t.clientX, clientY: t.clientY }), 500);
  };
  const touchEnd = () => clearTimeout(longPress.current);
  return (
    <div onClick={selecting ? onToggle : undefined} onContextMenu={onMenu} onTouchStart={touchStart} onTouchEnd={touchEnd} onTouchMove={touchEnd}
      className={`flex my-1 ${mine ? "justify-end" : "justify-start"} ${selected ? "bg-[#385C4B]/15 -mx-2 px-2 sm:-mx-4 sm:px-4" : ""} ${selecting ? "cursor-pointer" : ""}`}
      data-testid={`ekyc-chat-msg-${m.id}`}>
      <div className={`max-w-[85%] sm:max-w-[65%] rounded-lg px-2.5 py-1.5 shadow-sm ${mine ? "bg-[#D9FDD3]" : "bg-white"}`}>
        {!mine && <div className="text-[11px] font-bold text-[#2F6F8F]">{m.sender_name}{m.sender_role === "admin" ? " · Admin" : ""}</div>}
        {m.deleted ? (
          <div className="text-sm italic text-[#6B6B61] flex items-center gap-1"><Ban className="w-3.5 h-3.5" />Ye message delete kar diya gaya</div>
        ) : (
          <>
            {m.record_card && <RecordCard c={m.record_card} />}
            {m.attachments?.map((a) => <Attachment key={a.file_id} a={a} />)}
            {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
          </>
        )}
        <div className="text-[10px] text-[#6B6B61] text-right mt-0.5">{timeOf(m.created_at)}</div>
      </div>
    </div>
  );
}

function RecordCard({ c }) {
  const color = c.status === "done" ? "border-emerald-500" : c.status === "failed" ? "border-[#C8553D]" : "border-amber-500";
  return (
    <div className={`border-l-4 ${color} bg-black/5 rounded px-2 py-1 mb-1 text-xs`} data-testid="ekyc-chat-record-card">
      <div className="font-bold">📋 {c.name || "—"}</div>
      <div className="font-mono">ID {c.consumer_id}{c.mobile ? ` · ${c.mobile}` : ""}</div>
      <div className="uppercase font-mono">{c.status}{c.fail_reason ? ` · ${c.fail_reason}` : ""}</div>
    </div>
  );
}

function useBlobUrl(fileId) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/ekyc/files/${fileId}`, { responseType: "blob" }).then((res) => { u = URL.createObjectURL(res.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [fileId]);
  return url;
}

function Attachment({ a }) {
  const isImg = (a.content_type || "").startsWith("image/");
  const url = useBlobUrl(a.file_id);
  if (isImg) {
    return url ? (
      <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt={a.name} className="rounded max-h-64 max-w-full mb-1 object-contain" data-testid={`ekyc-chat-img-${a.file_id}`} />
      </a>
    ) : <div className="w-40 h-24 bg-black/5 rounded mb-1 animate-pulse" />;
  }
  return (
    <a href={url || undefined} download={a.name} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 bg-black/5 rounded px-2 py-1.5 mb-1 text-xs" data-testid={`ekyc-chat-file-${a.file_id}`}>
      <FileText className="w-5 h-5 text-[#C8553D]" />
      <span className="truncate max-w-[180px]">{a.name}</span>
      <span className="text-[#6B6B61]">{Math.ceil(a.size / 1024)} KB</span>
    </a>
  );
}

function Composer({ group, onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pick = async (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/ekyc/chat/${group.id}/upload`, fd);
        setFiles((x) => [...x, data]);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    setBusy(false);
  };
  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/ekyc/chat/${group.id}/messages`, { text, attachments: files });
      setText("");
      setFiles([]);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-[#EDEDE5] p-2 bg-[#F5F5F0]">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ekyc-chat-pending-files">
          {files.map((f) => (
            <span key={f.file_id} className="text-xs bg-white border border-[#DCDCD3] rounded px-2 py-1 flex items-center gap-1">
              📎 {f.name}<button onClick={() => setFiles((x) => x.filter((y) => y.file_id !== f.file_id))}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-black/5" title="Photo / File" data-testid="ekyc-chat-attach">
          <Paperclip className="w-5 h-5 text-[#6B6B61]" />
        </button>
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.csv,.txt,.xlsx,.xls" className="hidden" onChange={pick} data-testid="ekyc-chat-file-input" />
        <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none rounded-2xl border border-[#DCDCD3] px-3 py-2 text-sm max-h-32 bg-white" data-testid="ekyc-chat-input" />
        <button onClick={send} disabled={busy} className="p-2.5 rounded-full bg-[#385C4B] text-white disabled:opacity-50" data-testid="ekyc-chat-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function BubbleMenu({ anchor, onClose, onSelect, onCopy, onDelete }) {
  const style = useMemo(() => ({
    left: Math.min(anchor.x, window.innerWidth - 190), top: Math.min(anchor.y, window.innerHeight - 160),
  }), [anchor]);
  const item = (Icon, label, fn, testid, red) => fn && (
    <button onClick={() => { fn(); onClose(); }} data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#F5F5F0] ${red ? "text-[#C8553D]" : ""}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100]" onMouseDown={onClose} onTouchStart={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div className="fixed z-[101] bg-white border border-[#DCDCD3] shadow-2xl rounded-xl py-1 w-[180px]" style={style} data-testid="ekyc-chat-context-menu">
        {item(CheckSquare, "Select", onSelect, "ekyc-ctx-select")}
        {item(Copy, "Copy", onCopy, "ekyc-ctx-copy")}
        {item(Trash2, "Delete for everyone", onDelete, "ekyc-ctx-delete", true)}
      </div>
    </>,
    document.body,
  );
}
